// ==============================================
// Device Sign-In Page (Scout onboarding magic link)
// ==============================================
//
// Landing page for the link emailed during Scout first-boot setup
// (?das=<session>&token=<magic-link-token>; older emails also carry &sn=<serial>, used only as a fallback).
//
// The invariant this page enforces: no Scout activates unless its account
// holds a password. On load, a side-effect-free context read (mail scanners
// prefetch emailed URLs — nothing is consumed until the button) decides
// which door to show:
//   new           → create a password (account + credential land together)
//   has_password  → prove the current password ("Forgot?" reuses the portal
//                   reset flow); wrong attempts never burn the emailed token
//   no_password   → existing account with no credential (magic-link or
//                   social sign-ups) → create one now
// Both gates live in one POST /signin/activate. A 409 device_limit_reached
// means gate 2 (plan capacity) failed: the response carries the user's
// Scouts, and the customer picks one to replace — with an explicit
// acknowledgment — or leaves to upgrade the plan.
// The Scout notices by itself; the user just walks back to it.
//
// @docs:
//   - docs/core/architecture/ONBOARDING_SIGNIN_FLOW.md  § Sequence, § Endpoints

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../services/api';
import type { DeviceSigninContext, DeviceSigninLimitScout } from '../../services/api';

type Phase = 'loading' | 'form' | 'working' | 'choose' | 'swapping' | 'reset_sent' | 'done' | 'fatal';

const ERROR_HELP: Record<string, string> = {
  expired: 'This link has expired — they only last 15 minutes. Press "Resend email" on your Scout and tap the new link.',
  already_used: 'This link has already been used. If your Scout is still waiting, press "Resend email" on it for a fresh link.',
  email_mismatch: 'This link was sent to a different email address than the one you are signed in with.',
  claimed_by_other: 'This Scout is registered to another account. If you bought it second-hand or believe this is wrong, contact support@vortexmarine.com.',
  already_registered: 'This Scout is already signed in — you can head back to it.',
  not_found: 'This sign-in request is no longer active. Press "Resend email" on your Scout to start again.',
  session_locked: 'Too many attempts — this link is locked. Reset your password, then press "Resend email" on your Scout for a fresh link.',
};

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: {
      code?: string;
      error?: string;
      attempts_left?: number;
      plan_name?: string | null;
      max_devices?: number | null;
      scouts?: DeviceSigninLimitScout[];
    };
  };
  message?: string;
}

/** "Last seen 2 hours ago" — coarse on purpose; it informs a choice, not a log. */
function lastSeenLabel(iso: string | null): string {
  if (!iso) return 'Never seen online';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return 'Online recently';
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return 'Online in the last hour';
  const hours = Math.floor(mins / 60);
  if (hours < 48) return `Last seen ${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  return `Last seen ${days} days ago`;
}

export function DeviceSignInPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('das');
  const serial = searchParams.get('sn');
  const token = searchParams.get('token');

  const [phase, setPhase] = useState<Phase>('loading');
  const [context, setContext] = useState<DeviceSigninContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);

  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);

  const [scouts, setScouts] = useState<DeviceSigninLimitScout[]>([]);
  const [limitInfo, setLimitInfo] = useState<{ plan_name: string | null; max_devices: number | null }>({ plan_name: null, max_devices: null });
  const [selectedScout, setSelectedScout] = useState<string | null>(null);
  const [acknowledged, setAcknowledged] = useState(false);
  const [replacedName, setReplacedName] = useState<string | null>(null);

  const linkComplete = useMemo(() => Boolean(sessionId && token), [sessionId, token]);

  // The vessel name captured in the startup guide leads; hardware is fine print.
  const vesselLabel = context?.vessel_name ? `${context.vessel_name}’s` : 'your';
  const hardwareId = context?.serial_number ?? serial;
  const deviceLabel = /^SG-[0-9A-F]{16}$/.test(hardwareId ?? '') ? 'Scout Gateway' : 'Scout';

  useEffect(() => {
    if (!sessionId || !token) return; // incomplete-link panel renders instead
    let cancelled = false;
    api.getDeviceSigninContext(sessionId)
      .then((ctx) => {
        if (cancelled) return;
        setContext(ctx);
        setPhase('form');
      })
      .catch((err: ApiErrorShape) => {
        if (cancelled) return;
        const code = err?.response?.data?.code;
        setPhase('fatal');
        setError((code && ERROR_HELP[code]) || err?.response?.data?.error || ERROR_HELP.not_found);
      });
    return () => { cancelled = true; };
  }, [sessionId, token]);

  const handleActivateError = (err: ApiErrorShape) => {
    const data = err?.response?.data;
    const code = data?.code;
    if (code === 'device_limit_reached') {
      // Gate 2: identity proven, plan full. The cookie session now exists,
      // which is the only reason the Scout list may be shown at all.
      setScouts(data?.scouts ?? []);
      setLimitInfo({ plan_name: data?.plan_name ?? null, max_devices: data?.max_devices ?? null });
      setPhase('choose');
      return;
    }
    if (code === 'wrong_password') {
      const left = data?.attempts_left;
      setPhase('form');
      setInlineError(
        `That password doesn’t match this account.${typeof left === 'number' ? ` ${left} attempt${left === 1 ? '' : 's'} left` : ''} — or reset your password below.`,
      );
      return;
    }
    if (code === 'weak_password') {
      setPhase('form');
      setInlineError('Passwords need at least 8 characters.');
      return;
    }
    setPhase('fatal');
    setError((code && ERROR_HELP[code]) || data?.error || ERROR_HELP.expired);
  };

  const handleActivate = async () => {
    if (!sessionId || !token || !context) return;
    setInlineError(null);

    const needsNewPassword = context.account_state !== 'has_password';
    if (needsNewPassword) {
      if (newPassword.length < 8) { setInlineError('Passwords need at least 8 characters.'); return; }
      if (newPassword !== confirmPassword) { setInlineError('These passwords don’t match.'); return; }
    } else if (!password) {
      setInlineError('Enter your password.');
      return;
    }

    setPhase('working');
    try {
      await api.activateDeviceSignin({
        session_id: sessionId,
        token,
        ...(needsNewPassword ? { new_password: newPassword } : { password }),
      });
      setPhase('done');
    } catch (err) {
      handleActivateError(err as ApiErrorShape);
    }
  };

  const handleSwap = async () => {
    if (!sessionId || !selectedScout || !acknowledged) return;
    setPhase('swapping');
    try {
      await api.swapDeviceSignin({ session_id: sessionId, replace_device_id: selectedScout });
      const replaced = scouts.find((s) => s.id === selectedScout);
      setReplacedName(replaced?.name || replaced?.serial_number || null);
      setPhase('done');
    } catch (err) {
      const e = err as ApiErrorShape;
      const code = e?.response?.data?.code;
      setPhase('fatal');
      setError((code && ERROR_HELP[code]) || e?.response?.data?.error || ERROR_HELP.expired);
    }
  };

  const handleForgotPassword = async () => {
    if (!sessionId) return;
    try {
      await api.forgotDeviceSigninPassword(sessionId);
      setPhase('reset_sent');
    } catch (err) {
      const e = err as ApiErrorShape;
      const code = e?.response?.data?.code;
      setPhase('fatal');
      setError((code && ERROR_HELP[code]) || e?.response?.data?.error || ERROR_HELP.expired);
    }
  };

  // ---- shared fragments ----

  const hardwareFinePrint = hardwareId ? (
    <p className="mt-5 text-sm text-slate-500 text-center">
      {deviceLabel} hardware ID <span className="font-mono text-slate-400">{hardwareId}</span>
    </p>
  ) : null;

  const footNote = (
    <p className="mt-6 text-sm text-slate-500 text-center">
      Didn&rsquo;t expect this? Someone may have mistyped their email on a new {deviceLabel} — just close this page;
      nothing happens without the button above.
    </p>
  );

  const spinner = (
    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
    </svg>
  );

  const passwordInput = (props: {
    id: string; label: string; value: string; onChange: (v: string) => void;
    placeholder?: string; autoComplete: string; hint?: string;
  }) => (
    <div className="mb-4">
      <label htmlFor={props.id} className="block text-sm font-semibold text-slate-300 mb-1.5">{props.label}</label>
      <input
        id={props.id}
        type={showPasswords ? 'text' : 'password'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={props.placeholder}
        autoComplete={props.autoComplete}
        className="w-full px-4 py-3 bg-slate-900/70 border border-slate-600 rounded-lg text-white text-base placeholder-slate-500 focus:outline-none focus:border-cyan-400"
      />
      {props.hint && <p className="mt-1.5 text-sm text-slate-500">{props.hint}</p>}
    </div>
  );

  const showPasswordToggle = (
    <label className="flex items-center gap-2 mb-4 cursor-pointer text-sm text-slate-400">
      <input
        type="checkbox"
        checked={showPasswords}
        onChange={(e) => setShowPasswords(e.target.checked)}
        className="w-4 h-4 accent-cyan-500"
      />
      Show passwords
    </label>
  );

  const bigButton = (label: string, workingLabel: string, onClick: () => void, disabled = false) => (
    <button
      onClick={onClick}
      disabled={phase === 'working' || phase === 'swapping' || disabled}
      className="w-full flex items-center justify-center gap-2 px-4 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl font-semibold text-lg hover:from-cyan-400 hover:to-blue-500 transition-all shadow-lg shadow-cyan-500/25 disabled:opacity-50"
    >
      {phase === 'working' || phase === 'swapping' ? (<>{spinner}{workingLabel}</>) : label.replace(/\bScout\b/g, deviceLabel)}
    </button>
  );

  // ---- door-specific card bodies ----

  const renderNewUserForm = () => (
    <>
      <h1 className="text-2xl font-bold text-white text-center mb-2">Create your account</h1>
      <p className="text-slate-400 text-base text-center mb-6">
        You&rsquo;re signing in {vesselLabel} new {deviceLabel} to <span className="font-mono text-slate-200">{context?.masked_email}</span>.
        Choose a password for your new MyPolaris account — then one tap finishes setup by itself.
      </p>
      {passwordInput({
        id: 'new-password', label: 'Choose a password', value: newPassword, onChange: setNewPassword,
        placeholder: 'At least 8 characters', autoComplete: 'new-password',
        hint: 'You’ll use it to sign in at app.vortexnav.com.',
      })}
      {passwordInput({
        id: 'confirm-password', label: 'Confirm password', value: confirmPassword, onChange: setConfirmPassword,
        autoComplete: 'new-password',
      })}
      {showPasswordToggle}
      {inlineError && <p className="mb-4 text-sm text-red-400">{inlineError}</p>}
      {bigButton('Create account & activate my Scout', 'Creating your account…', handleActivate)}
      {footNote}
      {hardwareFinePrint}
    </>
  );

  const renderNoPasswordForm = () => (
    <>
      <h1 className="text-2xl font-bold text-white text-center mb-2">Secure your account</h1>
      <p className="text-slate-400 text-base text-center mb-4">
        Your MyPolaris account <span className="font-mono text-slate-200">{context?.masked_email}</span> doesn&rsquo;t
        have a password yet. Create one to activate {vesselLabel} new {deviceLabel}.
      </p>
      {(context?.social_providers.length ?? 0) > 0 && (
        <div className="mb-5 p-3.5 bg-cyan-500/10 border border-cyan-500/40 rounded-lg text-cyan-200 text-sm">
          Your existing sign-in keeps working — this adds a password alongside it.
        </div>
      )}
      {passwordInput({
        id: 'new-password', label: 'Choose a password', value: newPassword, onChange: setNewPassword,
        placeholder: 'At least 8 characters', autoComplete: 'new-password',
      })}
      {passwordInput({
        id: 'confirm-password', label: 'Confirm password', value: confirmPassword, onChange: setConfirmPassword,
        autoComplete: 'new-password',
      })}
      {showPasswordToggle}
      {inlineError && <p className="mb-4 text-sm text-red-400">{inlineError}</p>}
      {bigButton('Set password & activate my Scout', 'Securing your account…', handleActivate)}
      {footNote}
      {hardwareFinePrint}
    </>
  );

  const renderHasPasswordForm = () => (
    <>
      <h1 className="text-2xl font-bold text-white text-center mb-2">Welcome back</h1>
      <p className="text-slate-400 text-base text-center mb-4">
        You&rsquo;re signing in {vesselLabel} new {deviceLabel} to <span className="font-mono text-slate-200">{context?.masked_email}</span>.
        Enter your MyPolaris password to activate it.
      </p>
      {context?.at_limit && (
        <div className="mb-5 p-3.5 bg-amber-500/10 border border-amber-500/40 rounded-lg text-amber-300 text-sm">
          Your {limitPlanLabel()} is at its {context.max_devices}-Scout limit. After you sign in, you&rsquo;ll choose a
          Scout for this one to replace.
        </div>
      )}
      {passwordInput({
        id: 'password', label: 'Your password', value: password, onChange: setPassword,
        autoComplete: 'current-password',
      })}
      {inlineError && <p className="mb-4 text-sm text-red-400">{inlineError}</p>}
      {bigButton('Sign in & activate my Scout', 'Checking your password…', handleActivate)}
      <button
        onClick={handleForgotPassword}
        className="block w-full mt-4 text-center text-cyan-400 hover:underline text-base"
      >
        Forgot your password?
      </button>
      {footNote}
      {hardwareFinePrint}
    </>
  );

  function limitPlanLabel() {
    const plan = context?.plan_name ?? limitInfo.plan_name;
    return plan ? `${plan} plan` : 'plan';
  }

  const renderChoose = () => {
    const chosen = scouts.find((s) => s.id === selectedScout);
    const chosenLabel = chosen?.name || chosen?.serial_number || 'the replaced Scout';
    const max = limitInfo.max_devices ?? context?.max_devices;
    return (
      <>
        <h1 className="text-2xl font-bold text-white text-center mb-3">Choose a Scout to replace</h1>
        <p className="text-center mb-4">
          <span className="inline-block px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-sm font-semibold">
            {limitPlanLabel()}{max ? ` · ${scouts.length} of ${max} Scouts in use` : ''}
          </span>
        </p>
        <p className="text-slate-400 text-base text-center mb-5">
          Your plan&rsquo;s slots are full. Pick the Scout that {vesselLabel} new one replaces.
        </p>
        <div role="radiogroup" aria-label="Scout to replace">
          {scouts.map((s) => (
            <label
              key={s.id}
              className={`flex items-center gap-3 p-3.5 mb-2.5 rounded-xl border cursor-pointer ${
                selectedScout === s.id ? 'border-cyan-400 bg-cyan-500/10' : 'border-slate-600 bg-slate-900/50'
              }`}
            >
              <input
                type="radio"
                name="replace-scout"
                value={s.id}
                checked={selectedScout === s.id}
                onChange={() => setSelectedScout(s.id)}
                className="w-4.5 h-4.5 accent-cyan-400"
              />
              <span className="min-w-0">
                <span className="block text-white text-base font-semibold">{s.name || s.serial_number}</span>
                <span className="block text-slate-400 text-sm">
                  <span className="font-mono">{s.serial_number}</span> · {lastSeenLabel(s.last_seen_at)}
                </span>
              </span>
            </label>
          ))}
        </div>
        <label className="flex items-start gap-2.5 mt-4 mb-4 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-1 w-4.5 h-4.5 accent-amber-400"
          />
          <span className="text-amber-300 text-sm leading-snug">
            I understand {chosenLabel} will be signed out and will stop navigating within a few minutes.
          </span>
        </label>
        {bigButton(
          chosen ? `Replace ${chosenLabel} & activate my Scout` : 'Replace & activate my Scout',
          'Replacing…',
          handleSwap,
          !chosen || !acknowledged,
        )}
        <a href="/dashboard/subscription" className="block mt-4 text-center text-cyan-400 hover:underline text-base">
          Need both? Upgrade your plan instead →
        </a>
        {hardwareFinePrint}
      </>
    );
  };

  const renderDone = () => (
    <div className="text-center">
      <div className="mb-6 p-4 bg-green-500/10 border border-green-500/50 rounded-lg text-green-400">
        <svg className="w-12 h-12 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="font-medium text-lg">
          {context?.vessel_name ? <>{context.vessel_name}&rsquo;s {deviceLabel} is signed in!</> : `Your ${deviceLabel} is signed in!`}
        </p>
        <p className="text-sm mt-2 text-green-300/80">
          Head back to your {deviceLabel === 'Scout Gateway' ? 'SG web display' : 'Scout'} — it finishes signing in by itself within a few seconds.{' '}
          {replacedName ? (
            <>{replacedName} has been signed out and its slot freed — its tracks and history stay in your account.</>
          ) : context?.account_state === 'has_password' ? (
            'Nothing more to do here.'
          ) : (
            'Your MyPolaris account is ready — sign in anytime at app.vortexnav.com with your email and the password you just set.'
          )}
        </p>
      </div>
      {/* Full-page navigation on purpose, not a client-side <Link>:
          activation replaced the cookie session (possibly swapping accounts),
          and the running app's auth state predates that. A soft navigate let
          admin UI from the OLD session fire queries as the NEW user — a burst
          of "Forbidden" toasts on first load. A hard load reboots the app
          with the new session. */}
      <a href={deviceLabel === 'Scout Gateway' ? '/dashboard/devices' : '/dashboard'} className="text-cyan-400 hover:underline font-medium">
        Open your MyPolaris dashboard
      </a>
      {hardwareFinePrint}
    </div>
  );

  const renderResetSent = () => (
    <>
      <h1 className="text-2xl font-bold text-white text-center mb-4">Check your email</h1>
      <div className="p-4 bg-cyan-500/10 border border-cyan-500/40 rounded-lg text-cyan-200 text-base">
        We&rsquo;ve sent a password-reset email to <span className="font-mono">{context?.masked_email}</span>.
        After you reset, come back to this link — if it has expired by then, press &ldquo;Resend email&rdquo; on
        your {deviceLabel} for a fresh one.
      </div>
      {hardwareFinePrint}
    </>
  );

  const renderBody = () => {
    if (!linkComplete) {
      return (
        <div className="p-4 bg-amber-500/10 border border-amber-500/40 rounded-lg text-amber-300 text-sm text-center">
          This link is incomplete. Open the most recent &ldquo;Sign in your Scout&rdquo; email and tap its button —
          or press &ldquo;Resend email&rdquo; on your {deviceLabel}.
        </div>
      );
    }
    if (phase === 'loading') {
      return (
        <div className="flex items-center justify-center gap-3 py-10 text-slate-400 text-base">
          {spinner}
          Checking this link…
        </div>
      );
    }
    if (phase === 'fatal') {
      return (
        <>
          <h1 className="text-2xl font-bold text-white text-center mb-2">Sign in your {deviceLabel}</h1>
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
          {footNote}
          {hardwareFinePrint}
        </>
      );
    }
    if (phase === 'done') return renderDone();
    if (phase === 'reset_sent') return renderResetSent();
    if (phase === 'choose' || phase === 'swapping') return renderChoose();
    if (context?.account_state === 'new') return renderNewUserForm();
    if (context?.account_state === 'no_password') return renderNoPasswordForm();
    return renderHasPasswordForm();
  };

  return (
    <div
      className="flex overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4"
      style={{ height: 'var(--vortex-usable-height, 100vh)' }}
    >
      <div className="w-full max-w-md m-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3 mb-4">
            <img src="/vortex-logo-white.svg" alt="Vortex Marine" className="w-10 h-10" />
            <span className="text-2xl font-bold text-white">Vortex Marine</span>
          </div>
        </div>

        {/* Card */}
        <div className="bg-slate-800/50 backdrop-blur-xl rounded-2xl border border-slate-700/50 p-8 shadow-xl">
          {renderBody()}
        </div>
      </div>
    </div>
  );
}

export default DeviceSignInPage;
